import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/shared";
import { toast } from "sonner";
import { Loader2, GitMerge, X } from "lucide-react";
import { useAIWorkspace } from "@/hooks/useAIWorkspace";
import { ProctorReplaySection } from "@/components/ProctorReplaySection";
import { SebCandidateCard } from "@/components/proctor/SebCandidateCard";
import { CandidateHeader, CAND_TABS } from "@/components/candidate/CandidateHeader";
import { OverviewTab } from "@/components/candidate/OverviewTab";
import { AnswersTab, CvTab } from "@/components/candidate/CandidateTabs";
import { DecisionBar } from "@/components/candidate/DecisionBar";
import { exportCandidateReport } from "@/lib/candidateReport";

function MergeDialog({ c, merge, onClose, onMerge }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="merge-dialog">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center justify-between"><h3 className="font-display font-semibold text-lg flex items-center gap-2"><GitMerge className="h-4 w-4 text-primary" /> Merge duplicate records</h3><button onClick={onClose} aria-label="Close" data-testid="merge-close"><X className="h-4 w-4 text-muted-foreground" /></button></div>
        <p className="text-sm text-muted-foreground">Pick the record to merge <strong className="text-foreground">{c.name}</strong> into — this record is then archived and an audit entry is recorded.</p>
        {merge.loading ? <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Finding matches…</div>
          : merge.list.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No other matching records found.</p>
          : <div className="space-y-2">{merge.list.map((d) => (
            <button key={d.id} onClick={() => onMerge(d.id)} data-testid={`merge-into-${d.id}`} className="w-full text-left rounded-lg border border-border hover:border-primary p-3 transition-colors">
              <div className="font-medium text-sm">{d.name} <span className="text-muted-foreground font-normal">· {d.job_title || "—"}</span></div>
              <div className="text-xs text-muted-foreground">{d.email} · {d.stage}{d.overall_score != null ? ` · score ${d.overall_score}` : ""}</div>
            </button>))}</div>}
      </div>
    </div>
  );
}

export default function CandidateIntelligence() {
  const { candId } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = CAND_TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "overview";
  const setTab = (t) => setParams(t === "overview" ? {} : { tab: t }, { replace: true });
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [job, setJob] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [brand, setBrand] = useState(null);
  const [regen, setRegen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [merge, setMerge] = useState(null);
  const openChat = useAIWorkspace();

  const load = () => { setErr(null); api.get(`/candidates/${candId}`).then((r) => setC(r.data)).catch((e) => setErr(e.message)); };
  useEffect(load, [candId]);
  useEffect(() => { api.get("/tenant/branding").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!c?.job_id) return;
    api.get(`/jobs/${c.job_id}`).then((r) => setJob(r.data)).catch(() => {});
    api.get(`/jobs/${c.job_id}/candidates`).then((r) => setSiblings([...r.data].filter((x) => x.stage !== "rejected" || x.id === candId).sort((a, b) => (b.overall_score ?? -1) - (a.overall_score ?? -1)))).catch(() => {});
  }, [c?.job_id, candId]);

  const regenDebrief = async () => {
    setRegen(true);
    try { const { data } = await api.post(`/candidates/${candId}/debrief`); setC((p) => ({ ...p, debrief: data })); toast.success("AI debrief generated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not generate debrief"); }
    finally { setRegen(false); }
  };
  const exportPdf = async () => { setExporting(true); try { await exportCandidateReport(c, job, brand); toast.success("Report exported"); } catch { toast.error("Could not export the report"); } finally { setExporting(false); } };
  const openMerge = async () => { setMerge({ loading: true, list: [] }); try { const { data } = await api.get(`/candidates/${candId}/duplicates`); setMerge({ loading: false, list: data }); } catch { setMerge(null); toast.error("Could not load duplicate records"); } };
  const doMerge = async (intoId) => { try { await api.post(`/candidates/${candId}/merge`, { into_id: intoId }); toast.success("Records merged — this record was archived"); setMerge(null); nav(-1); } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); } };
  const copilot = () => openChat("Suggest fair, evidence-grounded interview questions for this candidate, with a reason for each question.", { mode: "Interview Copilot", context: { candidate_id: candId }, previewUrl: `/recruiter/candidate/${candId}` });

  if (err) return <div className="recruiter-page"><ErrorState message={err} onRetry={load} /></div>;
  if (!c || c.id !== candId) return <LoadingState label="Assembling candidate intelligence…" />;

  return (
    <div className="flex flex-col min-h-[calc(100dvh-60px)]" data-testid="candidate-intelligence">
      <CandidateHeader c={c} job={job} siblings={siblings} tab={tab} setTab={setTab} onMerge={openMerge} />
      <div className="flex-1 pb-6">
        {tab === "overview" && <OverviewTab c={c} onRegen={regenDebrief} regen={regen} onCopilot={copilot} />}
        {tab === "answers" && <AnswersTab c={c} />}
        {tab === "proctoring" && <div className="app-container py-6"><SebCandidateCard candId={candId} /><ProctorReplaySection candId={candId} /></div>}
        {tab === "cv" && <CvTab candId={candId} />}
      </div>
      <DecisionBar c={c} onChanged={(patch) => setC((p) => ({ ...p, ...patch }))} onExport={exportPdf} exporting={exporting} />
      {merge && <MergeDialog c={c} merge={merge} onClose={() => setMerge(null)} onMerge={doMerge} />}
    </div>
  );
}
